import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Loader2, Scale, ChevronDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { QuestionProgress } from './types';

interface JurisprudenceSearchProgressProps {
  jobId: string;
  questions: QuestionProgress[];
}

/**
 * Phase percentages:
 * fondements(0-24) → keywords(25-32) → search(33-41) → scoring(42-46) → fetch(47-59) → relevance(60-99) → done(100)
 */
const PHASE_BASE: Record<string, number> = {
  fondement_enrichment: 0,
  keywords: 25,
  search: 33,
  scoring: 42,
  fetch: 47,
  relevance_analysis: 60,
  // synthesizing: heartbeat sent by backend during final response generation
  // to prevent the 60s inactivity timeout. Individual questions are already at
  // 100%, so base=100 keeps the bars full while the global synthesis animation
  // (90→100%) runs separately.
  synthesizing: 100,
  done: 100,
};

const PHASE_CAP: Record<string, number> = {
  fondement_enrichment: 24,
  keywords: 32,
  search: 41,
  scoring: 46,
  fetch: 59,
  relevance_analysis: 99,
  synthesizing: 100,
  done: 100,
};

const PHASE_LABELS: Record<string, string> = {
  fondement_enrichment: 'Recherche des fondements juridiques',
  keywords: 'Analyse de la question',
  search: 'Recherche dans Judilibre',
  scoring: 'Filtrage des arrêts',
  fetch: 'Récupération des décisions complètes',
  relevance_analysis: 'Analyse approfondie de la pertinence',
  synthesizing: 'Synthèse de la réponse finale',
  done: 'Terminé',
};

function getPhaseBase(phase: string): number {
  return PHASE_BASE[phase] ?? 0;
}

function getPhaseCap(phase: string): number {
  return PHASE_CAP[phase] ?? 100;
}

/**
 * Persists each question's animated display value across component unmounts.
 *
 * The bar's displayed % lives in local React state and is driven by a per-second
 * ticker. Any unmount (collapse/expand of the panel, switching conversation,
 * leaving and re-entering the agent view) discards that local state. On remount
 * the backend only re-supplies the *coarse* phase progress (which sits at or
 * below the phase floor), so without this cache the bar visibly snaps back to
 * the phase floor — losing all the locally-accumulated progress.
 *
 * Keyed by `${jobId}:${questionId}`. Lives outside React, so it survives any
 * unmount within the SPA session; a hard browser reload clears it (acceptable —
 * fresh page). Entries are deleted once their question reaches the 'done' phase
 * to avoid unbounded growth across successive searches.
 */
const animatedProgressCache = new Map<string, number>();

export function JurisprudenceSearchProgress({
  jobId,
  questions,
}: JurisprudenceSearchProgressProps) {
  // Ouvert pendant le déroulé de la recherche : l'avocat suit l'avancement
  // (pourcentage, barres de chargement par question). Une fois terminée, ce bloc
  // de progression est remplacé in-place par `JurisprudenceSearchResults` (lui,
  // collapsed par défaut) — la bascule de composant porte le « terminé = replié »,
  // pas besoin d'auto-collapse ici. (issue #289 — ajusté 2026-06-17)
  const [isOpen, setIsOpen] = useState(true);

  const allDone = questions.length > 0 && questions.every(q => q.progress >= 100);

  // Estimate elapsed time from average question progress so the countdown
  // survives component remounts instead of resetting to 0.
  const TOTAL_SECONDS = 2 * 60;
  const avgProgress = questions.length > 0
    ? questions.reduce((sum, q) => sum + q.progress, 0) / questions.length
    : 0;
  const estimatedElapsed = Math.round((avgProgress / 100) * TOTAL_SECONDS);
  const [elapsedSeconds, setElapsedSeconds] = useState(estimatedElapsed);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  /** Countdown timer: starts on mount, stops when all questions are done */
  useEffect(() => {
    if (allDone) {
      if (timerRef.current) clearInterval(timerRef.current);
      return undefined;
    }
    timerRef.current = setInterval(() => setElapsedSeconds(s => s + 1), 1000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [allDone]);

  const remainingSeconds = Math.max(0, TOTAL_SECONDS - elapsedSeconds);
  const remainingMinutes = Math.ceil(remainingSeconds / 60);
  // Past the estimate, the search is still legitimately running — le wall-time réel
  // peut dépasser l'estimation. L'ancien libellé "plus que quelques secondes" laissait
  // croire à une complétion imminente et se lisait comme un blocage/timeout. On bascule
  // en état indéterminé, honnête quand la recherche déborde de l'estimation.
  const countdownLabel = remainingSeconds <= 0
    ? 'recherche toujours en cours…'
    : remainingMinutes === 1
      ? '~1 minute restante'
      : `~${remainingMinutes} minutes restantes`;

  return (
    <div className="rounded-lg border border-grey-200 bg-white overflow-hidden">
      {/* Header */}
      <div
        className="flex items-center gap-2 px-4 py-2.5 border-b border-grey-100 bg-grey-50/60 cursor-pointer"
        onClick={() => setIsOpen(o => !o)}
      >
        <Loader2 className="w-3.5 h-3.5 text-grey-400 animate-spin flex-shrink-0" />
        <Scale className="w-3.5 h-3.5 text-grey-400 flex-shrink-0" />
        <span className="text-xs font-medium text-grey-600">
          Recherche de jurisprudence
          {!allDone && (
            <span className="text-grey-400 font-normal"> ({countdownLabel})</span>
          )}
        </span>
        <span className="text-[11px] italic text-grey-400 whitespace-nowrap">
          ({isOpen ? 'Cliquer ici pour réduire' : 'Cliquer ici pour dérouler'})
        </span>
        <ChevronDown
          className={cn(
            'ml-auto w-3 h-3 text-grey-600 flex-shrink-0 transition-transform duration-200',
            isOpen && 'rotate-180',
          )}
        />
      </div>

      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            transition={{ duration: 0.2, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="p-3 space-y-4">
              {questions.map((q, index) => (
                <QuestionProgressRow key={q.id} jobId={jobId} question={q} index={index} />
              ))}

              {/* Final synthesis indicator — shown when all questions are done */}
              {allDone && (
                <motion.div
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: 0.2 }}
                  className="flex items-center gap-2 pt-2 border-t border-grey-100"
                >
                  <span className="text-xs font-medium text-grey-600">
                    Génération de la réponse finale…
                  </span>
                  <Loader2 className="w-3 h-3 text-grey-400 animate-spin ml-auto flex-shrink-0" />
                </motion.div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/**
 * Animates progress smoothly: jumps to phase base, then +1%/sec until cap.
 *
 * Uses an interval that ticks every second, incrementing displayed progress
 * by 1% until it reaches the cap for the current phase. When the backend
 * sends a new phase event, the displayed value jumps to the new phase base.
 */
function QuestionProgressRow({
  jobId,
  question,
  index,
}: {
  jobId: string;
  question: QuestionProgress;
  index: number;
}) {
  const phase = question.phase ?? 'fondement_enrichment';
  const phaseBase = getPhaseBase(phase);
  const phaseCap = getPhaseCap(phase);
  const cacheKey = `${jobId}:${question.id}`;

  // Seed from the cross-unmount cache first so the locally-animated value survives
  // collapse/expand, conversation switch, or leaving the agent view. Fall back to
  // the backend-reported coarse progress on a genuine first mount. Floor at
  // phaseBase so the bar never shows less than the current phase minimum, and so
  // it jumps forward (not backward) if the phase advanced while unmounted.
  const initialDisplay = phase === 'done'
    ? 100
    : Math.max(phaseBase, animatedProgressCache.get(cacheKey) ?? question.progress);
  const [displayProgress, setDisplayProgress] = useState(initialDisplay);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const prevPhaseRef = useRef(phase);

  /** When phase changes, jump to the new base immediately */
  useEffect(() => {
    if (phase !== prevPhaseRef.current) {
      prevPhaseRef.current = phase;
      setDisplayProgress(phaseBase);
    }
  }, [phase, phaseBase]);

  /** Tick +1%/sec (0.5%/sec for relevance_analysis — longer phase) */
  useEffect(() => {
    if (phase === 'done') {
      setDisplayProgress(100);
      return undefined;
    }

    const FONDEMENT_INCREMENTS = [0, 1, 2];
    const RELEVANCE_INCREMENTS = [0.8, 1.0, 1.2];

    intervalRef.current = setInterval(() => {
      setDisplayProgress(prev => {
        if (prev >= phaseCap) return prev;
        const pick = (arr: number[]) => arr[Math.floor(Math.random() * arr.length)];
        const increment = phase === 'fondement_enrichment'
          ? pick(FONDEMENT_INCREMENTS)
          : phase === 'relevance_analysis'
          ? pick(RELEVANCE_INCREMENTS)
          : 1.5;
        return prev + (increment ?? 1);
      });
    }, 1000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [phase, phaseCap]);

  /**
   * Mirror the animated value into the cross-unmount cache on every change so a
   * later remount can reseed from it. Delete the entry once 'done' is reached:
   * a finished bar always reseeds to 100 via initialDisplay, so keeping it would
   * only leak memory across successive searches.
   */
  useEffect(() => {
    if (phase === 'done') {
      animatedProgressCache.delete(cacheKey);
    } else {
      animatedProgressCache.set(cacheKey, displayProgress);
    }
  }, [displayProgress, phase, cacheKey]);

  const isDone = phase === 'done';
  const phaseLabel = PHASE_LABELS[phase] ?? phase;
  const displayPct = `${Math.round(displayProgress)}%`;

  return (
    <div className="space-y-1.5">
      {/* Question text — wraps fully */}
      <div className="flex items-start gap-2">
        <span className="flex-shrink-0 text-xs font-bold text-grey-400 mt-0.5 tabular-nums">
          {String(index + 1).padStart(2, '0')}
        </span>
        <p className="text-xs text-grey-800 leading-relaxed">
          {question.questionText}
        </p>
      </div>

      {/* Progress bar + phase label + percentage */}
      <div className="flex items-center gap-2">
        <div className="flex-1 h-[3px] bg-grey-100 rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full bg-grey-900"
            initial={{ width: 0 }}
            animate={{ width: `${displayProgress}%` }}
            transition={{ duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] }}
          />
        </div>
        {!isDone && (
          <span className="text-xs text-grey-400 italic whitespace-nowrap">{phaseLabel}</span>
        )}
        {isDone && (
          <span className="text-xs text-grey-800 font-medium">Terminé</span>
        )}
        <span className="text-xs font-mono font-semibold tabular-nums w-9 text-right text-grey-800 flex-shrink-0">
          {displayPct}
        </span>
      </div>
    </div>
  );
}
